import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Separator } from "../components/ui/separator";
import { GuidedDashboard } from "../components/GuidedDashboard";
import { DarkDashboardMock } from "../components/DarkDashboardMock";
import { BrandMark, BrandWordmark } from "../components/BrandLogo";
import { SiteFooter } from "../components/SiteFooter";
import { Reveal } from "../components/Reveal";
import { API } from "../lib/config";
import {
  MessageCircle, Inbox, Megaphone, Globe, ShieldCheck, Repeat,
  ArrowRight, ClipboardCheck, BellRing, Timer,
  PhoneCall, CheckCircle2, Sparkles, LayoutDashboard, MapPin, BadgeCheck, AlertCircle
} from "lucide-react";

const recordIntent = (source) => {
  axios.post(`${API}/deposit-intent`, { source }).catch(() => {});
};

// Primary CTA: --brand teal fill, --paper text, everywhere it appears.
const PRIMARY_CTA = "bg-[var(--brand)] text-[var(--paper)] hover:bg-[var(--brand-strong)] focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2";

const SectionTitle = ({ title, sub, subMax = "", className = "" }) => (
  <div className={`max-w-2xl ${className} mb-10`}>
    <h2 className="text-2xl sm:text-3xl font-semibold text-foreground">{title}</h2>
    {sub && <p className={`text-muted-foreground mt-3 leading-relaxed ${subMax}`}>{sub}</p>}
  </div>
);

const JOURNEY = [
  {
    label: "Enquiry",
    leak: "A parent messages after hours, then keeps looking while the details sit across inboxes and phones.",
    safeguard: "Bring every enquiry into one visible follow-up flow.",
  },
  {
    label: "Trial",
    leak: "Availability, teacher fit, and parent timing are confirmed in separate conversations, with no single next step.",
    safeguard: "Turn back-and-forth into one guided booking path.",
  },
  {
    label: "Renewal",
    leak: "Renewals, waitlists, and sibling opportunities depend on someone remembering at exactly the right moment.",
    safeguard: "Build the next conversation into the family journey.",
  },
];

const METRICS = [
  { label: "First response time", value: "3m", hint: "How fast parents hear back", points: "0,8 14,12 28,10 42,16 56,14 70,19 84,17 100,22" },
  { label: "Enquiry-to-trial conversion", value: "25%", hint: "Enquiries that become trials", points: "0,22 14,18 28,20 42,14 56,16 70,10 84,12 100,6" },
  { label: "Trial show rate", value: "76%", hint: "Booked trials that actually attend", points: "0,18 14,20 28,14 42,16 56,12 70,14 84,9 100,10" },
  { label: "Trial-to-enrolment conversion", value: "59%", hint: "Trials that convert to paid", points: "0,20 14,16 28,18 42,13 56,15 70,11 84,13 100,8" },
  { label: "Term re-enrolment rate", value: "74%", hint: "Families renewing each term", points: "0,16 14,18 28,13 42,15 56,11 70,13 84,8 100,9" },
];

const lastPoint = (points) => {
  const pair = points.trim().split(" ").pop().split(",");
  return { x: Number(pair[0]), y: Number(pair[1]) };
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeMetric, setActiveMetric] = useState(1);
  const sentinelRef = useRef(null);

  // Nav shadow via IntersectionObserver sentinel. No window scroll listeners.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const goReserve = (source) => {
    recordIntent(source);
    navigate("/reserve");
  };

  return (
    <div className="relative min-h-screen bg-background noise-overlay overflow-x-clip">
      <div ref={sentinelRef} aria-hidden="true" className="absolute top-0 left-0 h-px w-px" />

      {/* ---------- Sticky nav ---------- */}
      <header className={`sticky top-0 z-50 bg-background border-b border-border transition-shadow ${scrolled ? "shadow-[var(--shadow-sm)]" : ""}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-3 h-16">
          <Link to="/" className="flex items-center gap-1.5 rounded-lg min-w-0 shrink focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" aria-label="EnquiryOps home" data-testid="nav-logo">
            <BrandMark size={32} />
            <BrandWordmark className="text-base md:text-lg" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#problem" className="hover:text-foreground transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]">The Problem</a>
            <a href="#solution" className="hover:text-foreground transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]">Solution</a>
            <a href="#dashboard" className="hover:text-foreground transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]">Dashboard</a>
            <a href="#pricing" className="hover:text-foreground transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]">Pricing</a>
            <a href="#faq" className="hover:text-foreground transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]">FAQ</a>
          </nav>
          <Button
            className={`w-auto shrink-0 whitespace-nowrap min-h-[44px] h-auto px-3.5 py-2.5 rounded-[18px] text-[clamp(13px,3.5vw,16px)] md:min-h-0 md:h-10 md:px-4 md:py-2 md:rounded-xl md:text-sm ${PRIMARY_CTA}`}
            data-testid="nav-reserve-slot-button"
            onClick={() => goReserve("nav")}
          >
            <span className="md:hidden">Reserve Founding Slot</span>
            <span className="hidden md:inline">Reserve Founding Centre Slot</span>
          </Button>
        </div>
      </header>

      {/* ---------- Hero ---------- */}
      <section className="hero-mist">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-16 sm:pt-20 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-semibold leading-[1.08] text-foreground">
              Convert More Parent Enquiries Into Enrolments
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              EnquiryOps gives each parent enquiry an owner and a next action, keeps WhatsApp follow-up moving, and shows where families stall before enrolment.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className={`rounded-xl shadow-[var(--shadow-md)] h-12 px-6 ${PRIMARY_CTA}`} data-testid="hero-reserve-slot-button" onClick={() => goReserve("hero")}>
                Reserve Founding Centre Slot <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <Button size="lg" variant="ghost" className="rounded-xl h-12 px-6 gap-2 text-[var(--ink)] hover:bg-[var(--surface-2)] focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-testid="hero-preview-dashboard-button" onClick={() => navigate("/demo")}>
                <LayoutDashboard className="h-[18px] w-[18px] text-[var(--brand)]" /> Preview Dashboard
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground" data-testid="hero-cta-microcopy">
              $199 refundable deposit. Credited to setup. Returned in full if we're not a fit.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {[{ icon: MessageCircle, label: "WhatsApp-first" }, { icon: MapPin, label: "Built for SG centres" }, { icon: BadgeCheck, label: "Founding Five Guarantee" }].map((c) => (
                <span key={c.label} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-white border border-border rounded-full px-3 py-1.5">
                  <c.icon className="h-3.5 w-3.5 text-accent" /> {c.label}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 min-w-0 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <GuidedDashboard />
          </div>
        </div>
      </section>

      {/* ---------- Founding offer strip ---------- */}
      <section className="bg-[var(--navy-900)]" data-testid="founding-scarcity-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-center">
          <p className="text-sm sm:text-base text-white font-medium">
            <span className="font-semibold">5 founding-centre slots.</span> $199 refundable deposit. Credited toward setup or first month.
          </p>
          <button onClick={() => goReserve("strip")} data-testid="strip-reserve-link" className="text-sm font-semibold text-[hsl(188_62%_75%)] hover:text-white transition-colors inline-flex items-center gap-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--paper)]">
            Reserve yours <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </section>

      {/* ---------- Problem ---------- */}
      <section id="problem" className="py-16 sm:py-20" data-testid="problem-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionTitle
              title="An enquiry stalls when no one owns the next step"
              sub="A quick reply starts the conversation. EnquiryOps assigns the owner, flags the next WhatsApp action and tracks the family from enquiry to enrolment."
              className="lg:max-w-5xl"
            />
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5">
              <Reveal className="h-full">
                <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] h-full">
                  <CardContent className="p-5">
                    <p className="text-sm font-semibold text-foreground mb-4">Where enquiries land today</p>
                    <div className="flex flex-wrap gap-2 mb-5">
                      {[{ icon: MessageCircle, label: "WhatsApp" }, { icon: Globe, label: "Website forms" }, { icon: Megaphone, label: "FB / IG ads" }, { icon: Inbox, label: "Staff inboxes" }, { icon: Sparkles, label: "Holiday camp campaigns" }, { icon: PhoneCall, label: "Calls & walk-ins" }].map((c) => (
                        <span key={c.label} className="inline-flex items-center gap-1.5 text-xs font-medium bg-secondary border border-border rounded-full px-3 py-1.5 text-foreground">
                          <c.icon className="h-3.5 w-3.5 text-accent" /> {c.label}
                        </span>
                      ))}
                    </div>
                    <Separator className="mb-4" />
                    <div className="space-y-2.5">
                      {["“Can I get back to you after I check with the teacher?” Never followed up", "Trial attended on Saturday. Nobody called on Monday", "Waitlisted parent forgotten when a seat opened", "Term ended. No renewal nudge went out"].map((line) => (
                        <div key={line} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="h-1.5 w-1.5 rounded-full bg-[var(--ink)] mt-1.5 shrink-0" />
                          {line}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Reveal>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Timer, title: "Slow first response", body: "Parents enquire at 3 centres at once. The centre that replies in minutes, not hours, usually wins the trial." },
                { icon: ClipboardCheck, title: "Trial coordination chaos", body: "Checking vacancy, getting teacher approval, offering a slot, confirming with the parent. Four manual steps, each one a drop-off point." },
                { icon: BellRing, title: "Follow-ups live in someone's head", body: "Post-trial decisions, waitlist offers, and renewal nudges depend on whoever remembers. People forget. Systems don't." },
                { icon: Repeat, title: "Revenue leaks quietly", body: "Every missed re-enrolment, sibling opportunity, and unsent review request is money your marketing already paid for." },
              ].map((c, i) => (
                <Reveal key={c.title} delay={i * 40} className="h-full">
                  <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow h-full">
                    <CardContent className="p-5">
                      <span className="h-14 w-14 rounded-full bg-[var(--surface-3)] flex items-center justify-center mb-4">
                        <c.icon className="h-6 w-6 text-[var(--ink)]" />
                      </span>
                      <p className="font-semibold text-foreground text-sm mb-1.5">{c.title}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                    </CardContent>
                  </Card>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Benchmark strip (dark) ---------- */}
      <section className="bg-[var(--navy-950)]" data-testid="benchmark-stats">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:items-center">
              <div className="md:col-span-4">
                <p className="text-sm text-[var(--navy-200)] leading-relaxed">
                  We benchmarked 100 tuition and enrichment businesses in Central Singapore. Stronger centres already had WhatsApp, enquiry forms, reviews, and basic tracking. The gap wasn't a missing CTA. It was the messy parent conversion path after the enquiry.
                </p>
              </div>
              <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { stat: "29 of 30", claim: "top-scoring centres had no self-serve calendar booking" },
                  { stat: "28 of 30", claim: "had no live chat or AI chat on their site" },
                  { stat: "13 of 30", claim: "showed no visible CRM or form automation" },
                ].map((s) => (
                  <div key={s.stat}>
                    <p className="font-heading text-3xl sm:text-4xl font-semibold tabular-nums text-[var(--paper)]">{s.stat}</p>
                    <p className="text-sm text-[var(--navy-200)] mt-1.5 leading-snug">{s.claim}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-6 text-[11px] text-[var(--navy-200)]">
              From a 100-business Central Singapore tuition &amp; enrichment benchmark, top 30 by overall digital presence score.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- Enrolment journey ---------- */}
      <section id="solution" className="py-16 sm:py-20" data-testid="enrolment-journey">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="max-w-2xl lg:max-w-5xl mb-10">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--brand)] mb-3" data-testid="journey-eyebrow">The enrolment journey</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-foreground">Give each enquiry an owner, a next action and a clear outcome</h2>
            </div>
          </Reveal>
          <Reveal>
            <div className="relative rounded-2xl border border-[var(--line)] bg-[var(--surface-1)] overflow-hidden">
              {/* stage rail: horizontal on desktop only; mobile uses in-flow divide-y separators */}
              <div aria-hidden="true" className="hidden md:block absolute top-[76px] left-8 right-8 h-px bg-[var(--line)]" />
              <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[var(--line)]">
                {JOURNEY.map((card, i) => (
                  <div
                    key={card.label}
                    data-testid={`journey-card-${card.label.toLowerCase()}`}
                    className="relative p-6 sm:p-8 transition-[background-color,box-shadow] duration-200 ease-out hover:bg-[var(--surface-2)] hover:shadow-[var(--shadow-md)]"
                  >
                    <div className="relative flex items-center gap-3 mb-6">
                      <span className="relative z-10 h-10 w-10 rounded-full bg-[var(--navy-950)] text-[var(--paper)] flex items-center justify-center font-heading font-semibold text-sm">{i + 1}</span>
                      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--ink)]">{card.label}</span>
                    </div>
                    <div className="space-y-5">
                      <div>
                        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--muted-ink)] mb-2">
                          <AlertCircle className="h-4 w-4" aria-hidden="true" /> Where it leaks
                        </p>
                        <p className="text-sm text-muted-foreground leading-relaxed">{card.leak}</p>
                      </div>
                      <div>
                        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--brand)] mb-2">
                          <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Safeguard
                        </p>
                        <p className="text-sm text-foreground leading-relaxed">{card.safeguard}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-center text-sm text-[var(--muted-ink)] mt-8">EnquiryOps flags the next WhatsApp action and shows where each family stalls before enrolment.</p>
            <p className="text-center text-sm text-muted-foreground mt-2">
              Want the mechanics? <Link to="/demo" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]" data-testid="solution-demo-link">Open the demo dashboard</Link> and walk the pipeline yourself.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- Metrics ---------- */}
      <section className="py-16 sm:py-20 bg-white border-y border-border" data-testid="kpi-cards">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-10">
              <h2 className="text-2xl sm:text-3xl font-semibold text-foreground max-w-xl lg:max-w-3xl">See where each family stalls before enrolling</h2>
              <p className="text-xs text-muted-foreground shrink-0 text-left max-w-[70ch]">See who is waiting, what your team needs to do next and how each trial ended.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {METRICS.map((m, i) => {
              const active = activeMetric === i;
              const dot = lastPoint(m.points);
              return (
                <Reveal key={m.label} delay={i * 40} className="h-full">
                  <button
                    type="button"
                    data-testid={`metric-card-${i}`}
                    aria-pressed={active}
                    aria-label={`${m.label}: ${m.value}. ${m.hint}`}
                    onMouseEnter={() => setActiveMetric(i)}
                    onFocus={() => setActiveMetric(i)}
                    onClick={() => setActiveMetric(i)}
                    className={`h-full w-full text-left rounded-2xl border p-4 sm:p-5 bg-[var(--surface-1)] transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 motion-reduce:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 ${active ? "border-[var(--brand)] shadow-[var(--shadow-md)]" : "border-[var(--line)] hover:shadow-[var(--shadow-sm)]"}`}
                  >
                    <p className="text-sm font-bold text-foreground leading-snug">{m.label}</p>
                    <p className="text-xs text-[var(--muted-ink)] mt-1">{m.hint}</p>
                    <p className="font-heading text-3xl font-semibold tabular-nums text-foreground mt-3">{m.value}</p>
                    <svg viewBox="0 0 100 28" className="mt-3 h-7 w-full" aria-hidden="true" preserveAspectRatio="none">
                      <polyline
                        points={m.points}
                        fill="none"
                        stroke={active ? "var(--brand)" : "var(--muted-ink)"}
                        strokeOpacity={active ? "1" : "0.45"}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                      />
                      {active && <circle cx={dot.x} cy={dot.y} r="3" fill="var(--brand)" />}
                    </svg>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Dashboard preview ---------- */}
      <section id="dashboard" className="py-16 sm:py-20" data-testid="dashboard-preview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-semibold text-foreground max-w-[24ch] lg:max-w-3xl">Move each enquiry toward a decision</h2>
              <p className="text-muted-foreground mt-3 leading-relaxed max-w-[60ch] lg:max-w-4xl">EnquiryOps flags the next WhatsApp action and tracks the family through trial, enrolment or a clear no.</p>
            </div>
          </Reveal>
          <Reveal>
            <DarkDashboardMock />
          </Reveal>
          <Reveal>
            <div className="mt-6 flex flex-col sm:flex-row items-center gap-3 justify-center">
              <Button size="lg" className={`rounded-xl h-12 px-6 ${PRIMARY_CTA}`} data-testid="preview-open-demo-button" onClick={() => navigate("/demo")}>
                Open the full interactive demo <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <p className="text-xs text-muted-foreground">No sign-up needed · loaded with realistic demo data</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Founding Five Guarantee band ---------- */}
      <section className="bg-[var(--navy-900)]" data-testid="guarantee-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-center gap-4">
          <span className="h-14 w-14 rounded-full bg-[var(--paper)] flex items-center justify-center shrink-0" aria-hidden="true">
            <ShieldCheck className="h-6 w-6 text-[var(--ink)]" />
          </span>
          <p className="text-sm sm:text-base text-white font-medium leading-relaxed max-w-[62ch] text-center sm:text-left">
            <span className="font-semibold">The Founding Five Guarantee:</span> more booked trials in your first 60 days than in the 60 days before, or we refund every dollar you've paid us, setup included, and keep working free until you get them.
          </p>
        </div>
      </section>

      {/* ---------- Pricing ---------- */}
      <section id="pricing" className="py-16 sm:py-20" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionTitle
              title="Reserve a founding-centre slot with a refundable deposit"
              className="lg:max-w-5xl"
              sub="We onboard five founding centres ourselves. If you proceed, we credit the $199 deposit toward your setup or first month. If EnquiryOps is not a fit, we refund it."
            />
          </Reveal>

          <Reveal>
            <Card className="rounded-2xl border-2 border-[var(--brand)] bg-[hsl(188_62%_97%)] shadow-[var(--shadow-md)] mb-8">
              <CardContent className="p-6 flex flex-col md:flex-row items-start md:items-center gap-5 justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-[var(--brand)] text-[var(--paper)] hover:bg-[var(--brand)]">Step 1</Badge>
                    <p className="font-heading font-semibold text-foreground">Founding Centre Deposit</p>
                  </div>
                  <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
                    $199, fully refundable. Reserves one of 5 founding-centre slots and books your fit call. If EnquiryOps is not a fit, you get it back. If you proceed, it is credited toward setup or your first month. Paid via Stripe, followed by a short fit call.
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <p className="text-3xl font-heading font-semibold text-foreground tabular-nums">$199</p>
                    <p className="text-xs text-muted-foreground">refundable deposit</p>
                  </div>
                  <Button className={`rounded-xl h-11 ${PRIMARY_CTA}`} data-testid="pricing-deposit-button" onClick={() => goReserve("pricing-deposit")}>
                    Reserve Founding Centre Slot
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Reveal>

          <Reveal>
            <Card className="rounded-2xl border-2 border-[var(--navy-900)] shadow-[var(--shadow-md)] relative mb-4">
              <span className="absolute -top-3 left-6">
                <Badge className="bg-[var(--navy-900)] text-[var(--paper)] hover:bg-[var(--navy-900)]">Step 2 · Founding cohort</Badge>
              </span>
              <CardContent className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex flex-col">
                  <p className="font-heading font-semibold text-lg text-foreground">Founding Pilot</p>
                  <p className="text-xs text-muted-foreground mb-4">5 slots only</p>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-heading font-semibold tabular-nums text-foreground">$399</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-5">+ $499 one-time setup (founding rate)</p>
                  <div className="bg-secondary border border-border rounded-xl p-4 mb-5" data-testid="pricing-what-you-pay">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2.5">What you actually pay</p>
                    <ul className="space-y-2">
                      {[
                        "Today: $199 refundable deposit",
                        "If EnquiryOps is a fit: $499 setup (your $199 is credited, so $300) + $399/mo",
                        "If EnquiryOps is not a fit: $199 back, you owe nothing",
                      ].map((line) => (
                        <li key={line} className="flex items-start gap-2 text-sm text-foreground leading-relaxed">
                          <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" /> {line}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button className={`rounded-xl w-full mt-auto ${PRIMARY_CTA}`} data-testid="pricing-founding-pilot-button" onClick={() => goReserve("pricing-founding pilot")}>
                    Reserve Founding Centre Slot
                  </Button>
                </div>
                <div className="flex flex-col">
                  <ul className="space-y-2.5 mb-5">
                    {[
                      "WhatsApp-first pipeline and automations for one location (extra branches quoted separately)",
                      "AI-assisted replies with human handoff, pilot scope",
                      "Personal onboarding, migration, and a founder-direct support line",
                      "Weekly conversion review",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="bg-secondary border border-border rounded-xl p-4 mt-auto" data-testid="pricing-guarantee-note">
                    <p className="text-sm text-foreground font-medium leading-relaxed">
                      Backed by the Founding Five Guarantee: results in 60 days or every dollar back, setup included.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]" data-testid="pricing-standard-row">
              <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
                <p className="text-sm font-medium text-foreground">Standard pricing after the founding cohort. From $499/mo + $999 setup.</p>
                <p className="text-xs text-muted-foreground shrink-0">Starter · Growth · Multi-Branch</p>
              </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground mt-4">Setup fees shown above. No long lock-ins. EnquiryOps earns the renewal.</p>
          </Reveal>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" className="py-16 sm:py-20 bg-white border-y border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionTitle title="Frequently asked questions" />
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {[
                { q: "What is enquiry management software for tuition and enrichment centres in Singapore?", a: "It is software that captures every parent enquiry from WhatsApp, forms, ads, and walk-ins in one place, replies quickly, checks trial vacancy and teacher fit, books the trial, and follows up automatically through enrolment and renewal. EnquiryOps is built specifically for Singapore tuition and enrichment centres." },
                { q: "Why not just Calendly?", a: "Open calendars let anyone book anything, including levels you can't teach and classes that are already full. Enrichment trials need a vacancy check and teacher approval before a slot is promised. EnquiryOps runs that approval step first, then books, so every trial that lands on your calendar is one your team already said yes to." },
                { q: "Can staff approve trial slots first?", a: "Yes, that's the core of the system. Qualified enquiries enter a slot coordination queue. Your teacher or centre manager approves the class and timing, then the parent gets the offer with a 24-hour hold. Nothing is booked without a human yes." },
                { q: "What if the class has no vacancy?", a: "The family moves to Waitlist Recovery instead of disappearing. The moment a seat opens, the first family in the queue gets an automatic offer with a time-limited hold. Waitlists become a revenue pipeline, not a graveyard." },
                { q: "Does this work with WhatsApp?", a: "WhatsApp is the primary channel. It's where Singapore parents live. First responses, trial reminders, follow-ups, renewal nudges, and review requests all run over WhatsApp (with SMS and email as backups)." },
                { q: "Do we replace our centre software?", a: "No. Keep your student management, scheduling, and billing software. EnquiryOps sits in front of it, handling everything from first enquiry to signed enrolment, then handing over. It's a conversion layer, not a replacement." },
                { q: "Is the deposit refundable?", a: "Fully. The $199 deposit reserves your founding slot and books a fit call. If either side decides it's not a fit, we refund it, no questions. If you proceed, the full amount is credited toward setup or your first month." },
              ].map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-sm sm:text-base font-semibold hover:no-underline rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="py-16 sm:py-20" data-testid="final-cta">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="bg-[var(--navy-900)] rounded-3xl px-6 py-12 sm:px-12 sm:py-16 text-center">
              <h2 className="text-2xl sm:text-4xl font-semibold text-white max-w-2xl mx-auto leading-tight">
                Reserve a founding-centre slot before the first cohort closes
              </h2>
              <p className="text-sm font-medium text-[var(--navy-200)] mt-3 max-w-2xl mx-auto leading-relaxed" data-testid="final-cta-guarantee-line">
                Backed by the Founding Five Guarantee: results in 60 days or every dollar back, setup included.
              </p>
              <p className="text-sm sm:text-base text-[var(--navy-200)] mt-4 max-w-xl mx-auto leading-relaxed">
                5 centres. Personal onboarding. $199 refundable deposit, credited toward setup or your first month if we proceed, returned in full if we don't.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button size="lg" className={`rounded-xl h-12 px-7 ${PRIMARY_CTA}`} data-testid="final-cta-reserve-button" onClick={() => goReserve("final-cta")}>
                  Reserve Founding Centre Slot <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-xl h-12 px-7 gap-2 border-[var(--paper)] bg-transparent text-[var(--paper)] hover:bg-[var(--navy-800)] hover:text-[var(--paper)] focus-visible:ring-2 focus-visible:ring-[var(--paper)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--navy-900)]" data-testid="final-cta-demo-button" onClick={() => navigate("/demo")}>
                  <LayoutDashboard className="h-[18px] w-[18px] text-[var(--brand)]" /> Preview Dashboard
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
